import { Injectable, signal, NgZone, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { CdsPopupService } from '@cds/ng-web-components/popup';
import { SessionStorageService } from './session-storage.service';
import { environment } from '@environments/environment';
import { fromEvent, merge, Subject } from 'rxjs';
import { takeUntil, throttleTime, tap } from 'rxjs/operators';

export interface SessionTimeoutConfig {
  timeoutDuration: number;
  activityThrottle: number;
  checkInterval: number;
}

@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {

  private readonly config: SessionTimeoutConfig = {
    timeoutDuration: environment.production
      ? 30 * 60 * 1000
      : 30 * 60 * 1000,
    activityThrottle: 10 * 1000,
    checkInterval: 1000
  };

  // ✅ State Signal
  private readonly _isActive = signal<boolean>(false);
  readonly isActive = this._isActive.asReadonly();

  // ✅ RxJS Subjects
  private readonly destroy$ = new Subject<void>();
  private readonly activityDetected$ = new Subject<void>();

  // ✅ Timers
  private timeoutTimer: any = null;

  // ✅ Activity Events
  private readonly ACTIVITY_EVENTS = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click',
    'wheel',
    'keydown',
    'touchmove',
    'touchend'
  ];

  private readonly matDialog = inject(MatDialog);
  private readonly cdsPopupService = inject(CdsPopupService, { optional: true });

  constructor(
    private router: Router,
    private sessionStorage: SessionStorageService,
    private ngZone: NgZone
  ) {
    this.initializeActivityListeners();
  }

  start(): void {
    if (this._isActive()) {
      return;
    }
    this._isActive.set(true);
    this.resetTimer();
    this.startActivityMonitoring();
  }

  stop(): void {
    this._isActive.set(false);
    this.clearTimer();
    this.destroy$.next();
  }

  reset(): void {
    if (!this._isActive()) {
      return;
    }
    this.activityDetected$.next();
  }

  isSessionValid(): boolean {
    const sessionData = this.sessionStorage.getSessionData();
    if (!sessionData) {
      return false;
    }

    const elapsed = Date.now() - sessionData.lastActivity;
    return elapsed < this.config.timeoutDuration;
  }

  // ========== PRIVATE METHODS ==========

  /**
   * ✅ Initialize activity listeners using RxJS
   */
  private initializeActivityListeners(): void {
    this.ngZone.runOutsideAngular(() => {
      const activityStreams$ = this.ACTIVITY_EVENTS.map(event =>
        fromEvent(document, event, { passive: true })
      );

      // ✅ Merge all activity streams and throttle
      merge(...activityStreams$)
        .pipe(
          throttleTime(this.config.activityThrottle),
          tap(() => {
            this.ngZone.run(() => {
              this.handleActivity();
            });
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();

      // ✅ Visibility change detection
      fromEvent(document, 'visibilitychange')
        .pipe(
          tap(() => {
            if (!document.hidden && this._isActive()) {
              this.ngZone.run(() => {
                this.checkSessionValidity();
              });
            }
          }),
          takeUntil(this.destroy$)
        )
        .subscribe();
    });
  }

  /**
   * ✅ Start monitoring activity events
   */
  private startActivityMonitoring(): void {
    this.activityDetected$
      .pipe(
        throttleTime(this.config.activityThrottle),
        tap(() => {
          this.sessionStorage.updateLastActivity();
          this.resetTimer();
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  /**
   * ✅ Handle activity event
   */
  private handleActivity(): void {
    if (!this._isActive()) {
      return;
    }
    this.activityDetected$.next();
  }

  /**
   * ✅ Reset the timeout timer
   */
  private resetTimer(): void {
    this.clearTimer();

    this.timeoutTimer = setTimeout(() => {
      this.handleTimeout();
    }, this.config.timeoutDuration);
  }

  /**
   * ✅ Clear the timeout timer
   */
  private clearTimer(): void {
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
  }

  /**
   * ✅ Check if session is still valid
   */
  private checkSessionValidity(): void {
    if (!this.isSessionValid()) {
      this.handleTimeout();
    }
  }

  /**
   * ✅ Handle session timeout - redirect to eclaim
   */
  private handleTimeout(): void {
    console.error('❌ Session expired due to inactivity');

    this.stop();
    this.closeAllOpenPopups();
    this.sessionStorage.clearAppData();
    this.router.navigate(['/eclaim']);
  }

  /**
   * ✅ Force-close any open Material dialogs and CDS popups so they don't
   * remain visible after the timeout-driven navigation.
   */
  private closeAllOpenPopups(): void {
    try {
      this.matDialog.closeAll();
    } catch {
      // ignore – nothing to close
    }

    try {
      this.cdsPopupService?.closeAllDialog();
    } catch {
      // ignore – CdsPopupService may not be available in all contexts
    }
  }
}